import { Button, Heading, IconButton, Input, Separator, Text, toast } from "@overmap-ai/blocks"
import { generateAbbreviation } from "@overmap-ai/common"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { type FormEventHandler, memo, useCallback, useState } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { LuDynamicIcon } from "../../components/Icons/LuDynamicIcon"
import type { IconType } from "../../components/Icons/typings"
import { IconColorPickerPopover } from "../../components/Popovers/IconColorPickerPopover"
import { DEFAULT_COLOR } from "../../constants"
import { useChangeEventHandler } from "../../hooks/utils"
import { modelIconNameMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/asset-types/create")

export const CreateAssetTypeView = memo(() => {
	const { organizationId } = routeApi.useParams()

	const navigate = useNavigate()
	const [icon, setIcon] = useState<IconType>(modelIconNameMapping.assetType)
	const [color, setColor] = useState<string>(DEFAULT_COLOR)
	const [assetTypeName, setAssetTypeName] = useState<string>("")

	const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
		(e) => {
			e.preventDefault()
			e.stopPropagation()

			if (!assetTypeName) {
				toast.error({
					title: "Name is required",
					description: "Please enter a name for the asset type.",
				})
				return
			}

			const [offlineAssetType, promise] = sdk.assetTypes.add({
				organization: organizationId,
				name: assetTypeName,
				abbr: generateAbbreviation(assetTypeName),
				icon: icon,
				color: color,
				order: 0,
				default_status: null,
				description: null,
			})

			void promise.then(({ organization }) => {
				void sdk.assetTypeStatuses.refreshStore(organization)
			})

			setAssetTypeName("")
			setIcon(modelIconNameMapping.assetType)
			setColor(DEFAULT_COLOR)

			void navigate({
				from: "/$organizationId/settings/asset-types/create",
				to: "/$organizationId/settings/asset-types/$assetTypeId",
				params: (prev) => ({
					...prev,
					assetTypeId: offlineAssetType.uuid,
				}),
			})
		},
		[assetTypeName, organizationId, icon, color, navigate],
	)

	const handleNameChange = useChangeEventHandler(setAssetTypeName)

	const handleColorChange = useCallback((color: string | null) => {
		if (!color) return
		setColor(color)
	}, [])

	const handleIconChange = useCallback((icon: IconType | null) => {
		if (!icon) return
		setIcon(icon)
	}, [])

	return (
		<ViewContainer maxWidth="2xl">
			<section className="flex h-max w-full flex-col gap-4">
				<div className="flex h-max w-full flex-col gap-1">
					<Heading size="xl">Create asset type</Heading>
					<Text size="sm" accentColor="base">
						Create a new asset type to track customized information on assets.
					</Text>
				</div>
				<Separator size="full" />
				<form className="w-full" onSubmit={handleSubmit}>
					<div className="flex w-full flex-col gap-4">
						<div className="flex w-full gap-2">
							<IconColorPickerPopover
								className="w-[340px]"
								color={color}
								selectedIcon={icon}
								onColorChange={handleColorChange}
								onIconChange={handleIconChange}
								size="sm"
							>
								<IconButton aria-label="icon" accentColor="base" variant="soft">
									<LuDynamicIcon icon={icon} color={color} />
								</IconButton>
							</IconColorPickerPopover>
							<Input.Root variant="outline" className="w-full max-w-[300px]">
								<Input.Field
									placeholder="Enter a name"
									value={assetTypeName}
									onChange={handleNameChange}
								/>
							</Input.Root>
						</div>
						<Button className="w-max" type="submit" variant="surface">
							Create
						</Button>
					</div>
				</form>
			</section>
		</ViewContainer>
	)
})
CreateAssetTypeView.displayName = "CreateIssueTypeView"
